import React, { useState } from 'react';
import { Edit2, Info, HeartPulse, Sparkles, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
const pulseScoreData = [{
  month: 'Apr',
  score: 82,
  isKeyEvent: false
}, {
  month: 'May',
  score: 85,
  isKeyEvent: false
}, {
  month: 'Jun',
  score: 88,
  isKeyEvent: false
}, {
  month: 'Jul',
  score: 90,
  event: 'Financial statement filed: Revenue up 15% YoY',
  isKeyEvent: true
}, {
  month: 'Aug',
  score: 87,
  isKeyEvent: false
}, {
  month: 'Sep',
  score: 89,
  event: 'UCC filing: New equipment financing secured',
  isKeyEvent: true
}, {
  month: 'Oct',
  score: 91,
  event: 'News: International expansion announced',
  isKeyEvent: true
}, {
  month: 'Nov',
  score: 88,
  isKeyEvent: false
}, {
  month: 'Dec',
  score: 92,
  event: 'Financial statement: Record Q4 revenue',
  isKeyEvent: true
}, {
  month: 'Jan',
  score: 88,
  event: 'People: Charlie Bucket promoted to Factory Manager',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 89,
  event: 'DBT improved from 8 to 5 days',
  isKeyEvent: true
}, {
  month: 'Mar',
  score: 88,
  isKeyEvent: false
}];
const healthScoreData = [{
  month: 'Apr',
  score: 90
}, {
  month: 'May',
  score: 91
}, {
  month: 'Jun',
  score: 92
}, {
  month: 'Jul',
  score: 93,
  event: 'Financial statement: Strong revenue growth',
  isKeyEvent: true
}, {
  month: 'Aug',
  score: 92
}, {
  month: 'Sep',
  score: 93,
  event: 'Financial statement: Z-Score improved to 4.2',
  isKeyEvent: true
}, {
  month: 'Oct',
  score: 94
}, {
  month: 'Nov',
  score: 93
}, {
  month: 'Dec',
  score: 94
}, {
  month: 'Jan',
  score: 95,
  event: 'Financial statement: Debt-to-equity ratio at 0.4x',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 94
}, {
  month: 'Mar',
  score: 94
}];
const paymentScoreData = [{
  month: 'Apr',
  score: 85
}, {
  month: 'May',
  score: 86
}, {
  month: 'Jun',
  score: 87
}, {
  month: 'Jul',
  score: 88
}, {
  month: 'Aug',
  score: 87
}, {
  month: 'Sep',
  score: 88
}, {
  month: 'Oct',
  score: 89,
  event: 'DBT decreased from 8 to 5 days (37.5% improvement)',
  isKeyEvent: true
}, {
  month: 'Nov',
  score: 90
}, {
  month: 'Dec',
  score: 89
}, {
  month: 'Jan',
  score: 90
}, {
  month: 'Feb',
  score: 91,
  event: 'Trade data: 6 months perfect payment record',
  isKeyEvent: true
}, {
  month: 'Mar',
  score: 90
}];
function PositiveIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-4085">
      <div className="w-1 h-1.5 bg-green-500 rounded-sm" data-id="element-4086" />
      <div className="w-1 h-2.5 bg-green-500 rounded-sm" data-id="element-4087" />
      <div className="w-1 h-3.5 bg-green-500 rounded-sm" data-id="element-4088" />
    </div>;
}
function NegativeIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-4089">
      <div className="w-1 h-1.5 bg-red-500 rounded-sm" data-id="element-4090" />
      <div className="w-1 h-2.5 bg-red-500 rounded-sm" data-id="element-4091" />
      <div className="w-1 h-3.5 bg-red-500 rounded-sm" data-id="element-4092" />
    </div>;
}
function ScoreTooltip({
  active,
  payload,
  label,
  color
}: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dotColor = data.score !== undefined ? getDotColor(data.score) : color;
    return <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-4 py-3 max-w-[250px]" data-id="element-4093">
        <div className="flex items-center justify-between mb-2" data-id="element-4094">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4095">
            {label}
          </p>
          <div className="flex items-center gap-1.5" data-id="element-4096">
            <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: dotColor
          }} data-id="element-4097" />
            <span className="text-sm font-bold text-gray-900" data-id="element-4098">
              {payload[0].value}
            </span>
          </div>
        </div>
        {data.event && <p className="text-sm text-gray-700 leading-snug border-t border-gray-100 pt-2 mt-1" data-id="element-4099">
            {data.event}
          </p>}
      </div>;
  }
  return null;
}
function getDotColor(score: number) {
  if (score <= 30) return '#ef4444';
  if (score <= 70) return '#eab308';
  return '#22c55e';
}
const CustomDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  if (payload.isKeyEvent) {
    return <g data-id="element-4100">
        <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0.2} className="animate-pulse" data-id="element-4101" />
        <circle cx={cx} cy={cy} r={6} fill="#ffffff" stroke={color} strokeWidth={3} data-id="element-4102" />
      </g>;
  }
  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} strokeWidth={2} data-id="element-4103" />;
};
const CustomActiveDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  return <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} data-id="element-4104" />;
};
export function WonkaOverviewTab() {
  const [healthExpanded, setHealthExpanded] = useState(true);
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-4105">
      {/* Top Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4106">
        {/* Credit Settings */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-1" data-id="element-4107">
          <div className="flex justify-between items-center mb-6" data-id="element-4108">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4109">Credit Settings</h3>
            <button className="w-8 h-8 bg-indigo-600 hover:bg-indigo-700 rounded-full flex items-center justify-center text-white transition-colors" data-id="element-4110">
              <Edit2 className="w-4 h-4" data-id="element-4111" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-4112">
            <div data-id="element-4113">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4114">
                Credit Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-4115">$100,000</p>
            </div>
            <div data-id="element-4116">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4117">
                Recommended Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-4118">$100,000</p>
            </div>
            <div data-id="element-4119">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4120">
                Outstanding Balance
              </p>
              <p className="text-sm text-gray-600" data-id="element-4121">$28,750</p>
            </div>
            <div data-id="element-4122">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4123">
                AP Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-4124">Grandpa Joe</p>
            </div>
            <div data-id="element-4125">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4126">
                Terms
              </p>
              <p className="text-sm text-gray-600" data-id="element-4127">Net 30</p>
            </div>
            <div data-id="element-4128">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4129">
                Purchasing Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-4130">Charlie Bucket</p>
            </div>
            <div data-id="element-4131">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4132">
                Late Pay Penalty
              </p>
              <p className="text-sm text-gray-600" data-id="element-4133">1.5%</p>
            </div>
            <div data-id="element-4134">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4135">
                Company Officer
              </p>
              <p className="text-sm text-gray-600" data-id="element-4136">Willy Wonka</p>
            </div>
            <div data-id="element-4137">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4138">
                Date Last Approved
              </p>
              <p className="text-sm text-gray-600" data-id="element-4139">1/10/2026</p>
            </div>
            <div data-id="element-4140">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-4141">
                Group
              </p>
              <p className="text-sm text-gray-600" data-id="element-4142">Food & Beverage</p>
            </div>
          </div>
        </div>

        {/* Pulse Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-2 flex flex-col" data-id="element-4143">
          <div className="flex items-center gap-2 mb-4" data-id="element-4144">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-4145">Pulse Score</h3>
          </div>
          <div className="flex gap-6 mb-6" data-id="element-4146">
            <div className="bg-green-100 rounded-lg p-4 flex flex-col items-center justify-center min-w-[120px] shrink-0" data-id="element-4147">
              <span className="text-4xl font-bold text-gray-900" data-id="element-4148">88</span>
              <span className="text-sm font-medium text-gray-800 mt-1" data-id="element-4149">
                Low Risk
              </span>
            </div>
            <p className="text-gray-600 text-base leading-relaxed pt-2" data-id="element-4150">
              Pulse Score of 88 indicates low risk. Score has remained
              consistently strong over the past year, driven by innovative
              product launches and excellent financial management.
            </p>
          </div>
          <div className="flex-1 min-h-[200px] relative" data-id="element-4151">
            <div className="absolute top-0 right-0 flex items-center gap-2 text-xs text-gray-500 z-10" data-id="element-4152">
              <div className="w-3 h-3 bg-black rounded-sm" data-id="element-4153"></div>
              Pulse Score
            </div>
            <ResponsiveContainer width="100%" height="100%" data-id="element-4154">
              <LineChart data={pulseScoreData} margin={{
              top: 20,
              right: 10,
              left: -20,
              bottom: 10
            }} data-id="element-4155">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-4156" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} dy={10} data-id="element-4157" />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-4158" />
                <Tooltip content={<ScoreTooltip data-id="element-4160" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2,
                strokeDasharray: '4 4'
              }} data-id="element-4159" />
                <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="4 4" data-id="element-4161" />
                <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="4 4" data-id="element-4162" />
                <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-4164" />} activeDot={<CustomActiveDot data-id="element-4165" />} data-id="element-4163" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-4166">
        <button onClick={() => setHealthExpanded(!healthExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-4167">
          <div className="flex items-center gap-3" data-id="element-4168">
            <HeartPulse className="w-5 h-5 text-gray-700" data-id="element-4169" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-4170">Health Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-green-500 bg-white ml-2" data-id="element-4171">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" data-id="element-4172"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-4173">94</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-4174" />
          </div>
        </button>

        {healthExpanded && <div className="p-5" data-id="element-4175">
            <p className="text-base text-gray-600 mb-6" data-id="element-4176">
              WONKA INDUSTRIES has a 0.3% probability of insolvency or
              bankruptcy in the next 12 months.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4177">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-4178">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-4179">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-4180">
                  <div className="flex items-start gap-2" data-id="element-4181">
                    <NegativeIndicator data-id="element-4182" />
                    <span className="text-sm text-gray-600" data-id="element-4183">
                      High R&D costs for new product development
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-4184">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-4185">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-4186">
                  <div className="flex items-start gap-2" data-id="element-4187">
                    <PositiveIndicator data-id="element-4188" />
                    <span className="text-sm text-gray-600" data-id="element-4189">
                      Strong revenue growth (15% YoY) and excellent Z-Score
                      (4.2)
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-4190">
                    <PositiveIndicator data-id="element-4191" />
                    <span className="text-sm text-gray-600" data-id="element-4192">
                      52 years in business with global distribution
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-4193">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4194">
                  <LineChart data={healthScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-4195">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-4196" />
                    <XAxis dataKey="month" hide data-id="element-4197" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-4198" />
                    <Tooltip content={<ScoreTooltip color="#22c55e" data-id="element-4200" />} data-id="element-4199" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-4202" />} activeDot={<CustomActiveDot data-id="element-4203" />} data-id="element-4201" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-4204">
        <button onClick={() => setPaymentExpanded(!paymentExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-4205">
          <div className="flex items-center gap-3" data-id="element-4206">
            <CreditCard className="w-5 h-5 text-gray-700" data-id="element-4207" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-4208">Payment Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-green-500 bg-white ml-2" data-id="element-4209">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" data-id="element-4210"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-4211">90</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-4212" />
          </div>
        </button>

        {paymentExpanded && <div className="p-5" data-id="element-4213">
            <p className="text-base text-gray-600 mb-6" data-id="element-4214">
              WONKA INDUSTRIES has a 2.1% probability of late payment,
              indicating very low risk.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4215">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-4216">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-4217">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-4218">
                  <div className="flex items-start gap-2" data-id="element-4219">
                    <span className="text-sm text-gray-600 italic" data-id="element-4220">
                      No significant negative factors
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-4221">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-4222">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-4223">
                  <div className="flex items-start gap-2" data-id="element-4224">
                    <PositiveIndicator data-id="element-4225" />
                    <span className="text-sm text-gray-600" data-id="element-4226">
                      Perfect payment record with DBT well below industry
                      average
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-4227">
                    <PositiveIndicator data-id="element-4228" />
                    <span className="text-sm text-gray-600" data-id="element-4229">
                      Strong cash reserves and liquidity
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-4230">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4231">
                  <LineChart data={paymentScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-4232">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-4233" />
                    <XAxis dataKey="month" hide data-id="element-4234" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-4235" />
                    <Tooltip content={<ScoreTooltip color="#22c55e" data-id="element-4237" />} data-id="element-4236" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-4239" />} activeDot={<CustomActiveDot data-id="element-4240" />} data-id="element-4238" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}